import { Listbox, Transition } from '@headlessui/react';
import { DropDownChevronDownIcon } from 'assets/icons';
import classnames from 'classnames';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { createPortal } from 'react-dom';

interface ListDropDownProps {
  className?: string;
  selectedItem: any;
  items: any[];
  setSelectedItem: (item: any) => void;
  hideDropDownIcon?: boolean;
  buttonStyles?: string;
  optionStyles?: string;
  wrapperStyles?: string;
  disabled?: boolean;
  onClick?: () => void;
  spanStyles?: string;
  optionsHeight?: string;
  placeholder?: string;
  itemStyle?: string;
  id: string;
  iconStyle?: string;
  emptyStateMessage?: string;
  prefixIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  spanComponent?: (item: string) => React.ReactNode;
  isPortal?: boolean;
  optionComponent?: (item: any) => React.ReactNode;
  idKey?: string;
  nameKey?: string;
}

const ListDropDown: FC<ListDropDownProps> = ({
  wrapperStyles,
  optionStyles,
  buttonStyles,
  selectedItem,
  setSelectedItem,
  items,
  disabled = false,
  onClick = null,
  spanStyles = '',
  optionsHeight = '',
  hideDropDownIcon = false,
  placeholder = '',
  itemStyle = '',
  id = 'list-dropdown',
  iconStyle = '',
  emptyStateMessage = 'No items found',
  prefixIcon,
  trailingIcon,
  spanComponent = null,
  isPortal = false,
  optionComponent = null,
  idKey = 'id',
  nameKey = 'name'
}) => {
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isPortal) return;

    // Find all scrollable ancestors
    const getScrollableAncestors = (element: Element): Element[] => {
      const ancestors: Element[] = [];
      let parent = element.parentElement;

      while (parent && parent !== document.body) {
        const computedStyle = window.getComputedStyle(parent);
        const overflowY = computedStyle.overflowY;
        const overflowX = computedStyle.overflowX;
        const classList = parent.classList;

        // Check for overflow styles or custom scroll classes
        const isScrollable =
          overflowY === 'auto' ||
          overflowY === 'scroll' ||
          overflowX === 'auto' ||
          overflowX === 'scroll' ||
          classList.contains('customNormalScroll') ||
          classList.contains('customTransparentScroll') ||
          classList.contains('overflow-auto') ||
          classList.contains('overflow-scroll') ||
          classList.contains('overflow-y-auto') ||
          classList.contains('overflow-y-scroll') ||
          classList.contains('overflow-x-auto') ||
          classList.contains('overflow-x-scroll');

        if (isScrollable) {
          ancestors.push(parent);
        }
        parent = parent.parentElement;
      }

      return ancestors;
    };

    // Throttle position updates to improve performance
    let throttleTimer: NodeJS.Timeout | null = null;
    const throttledUpdatePosition = () => {
      if (throttleTimer) return;

      throttleTimer = setTimeout(() => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          if (rect.width > 0) {
            // Use getBoundingClientRect directly for portal positioning
            // since it gives us the position relative to the viewport
            setButtonPosition({
              top: rect.bottom,
              left: rect.left,
              width: rect.width
            });
          }
        }
        throttleTimer = null;
      }, 16); // ~60fps
    };

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        if (rect.width > 0) {
          // Use getBoundingClientRect directly for portal positioning
          // since it gives us the position relative to the viewport
          setButtonPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width
          });
        }
      }
    };

    let scrollableAncestors: Element[] = [];
    const resizeObserver = new ResizeObserver(updatePosition);

    if (buttonRef.current) {
      resizeObserver.observe(buttonRef.current);
      scrollableAncestors = getScrollableAncestors(buttonRef.current);
      // Initial position update
      updatePosition();
    }

    // Add scroll listeners to all scrollable ancestors and window
    window.addEventListener('scroll', throttledUpdatePosition);
    scrollableAncestors.forEach(ancestor => {
      ancestor.addEventListener('scroll', throttledUpdatePosition);
    });

    return () => {
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
      resizeObserver.disconnect();
      window.removeEventListener('scroll', throttledUpdatePosition);
      scrollableAncestors.forEach(ancestor => {
        ancestor.removeEventListener('scroll', throttledUpdatePosition);
      });
    };
  }, [isPortal]);

  const renderOptions = () => {
    const options = (
      <Listbox.Options
        className={twMerge(
          `${isPortal ? 'fixed' : 'absolute'} ${isPortal ? 'z-[1000]' : 'z-[60]'} mt-[2px] 
          ${optionsHeight !== '' ? `${optionsHeight} overflow-auto` : 'h-fit'}
          w-full min-w-[200px] rounded-md border
          border-borderGray bg-primary
          px-4 shadow-md sm:px-0`,
          classnames({ [optionStyles]: optionStyles })
        )}
        style={
          isPortal
            ? {
                top: `${buttonPosition.top}px`,
                left: `${buttonPosition.left}px`,
                width: `${buttonPosition.width}px`
              }
            : undefined
        }>
        {items.length > 0 ? (
          items.map(
            (item, index) =>
              optionComponent ? (
                optionComponent(item)
              ) : (
                <Listbox.Option
                  key={item[idKey] ?? id + index}
                  className={({ active }) =>
                    `text-primaryText font-semibold text-sbase relative cursor-pointer select-none ${active ? '' : ''}`
                  }
                  value={item}>
                  {({ selected }) => (
                    <span className={`block truncate py-2 pl-5 pr-4 ${selected ? '' : ''} ${itemStyle}`}>
                      {item[nameKey]}
                    </span>
                  )}
                </Listbox.Option>
              )
            // eslint-disable-next-line function-paren-newline
          )
        ) : (
          <div className="py-3 pl-5 pr-4 text-accentText">{emptyStateMessage}</div>
        )}
      </Listbox.Options>
    );

    if (isPortal) {
      return createPortal(
        <div id="root">
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            {options}
          </Transition>
        </div>,
        document.getElementById('portal-root')
      );
    }

    return (
      <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
        {options}
      </Transition>
    );
  };

  return (
    <div className={`w-full ${wrapperStyles}`}>
      <Listbox value={selectedItem} onChange={setSelectedItem} disabled={disabled && !onClick}>
        <div className="relative mt-1">
          <Listbox.Button
            disabled={disabled && !onClick}
            className={twMerge(
              ` 
              relative w-full cursor-default rounded-[4px] bg-white 
              py-4 px-5 text-left focus:outline-none 
            focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white
              focus-visible:ring-offset-2 
              `,
              classnames({ [buttonStyles]: buttonStyles })
            )}
            onClick={e => {
              if (disabled && onClick) {
                e.preventDefault();
                onClick();
              }
            }}
            id={id}
            ref={buttonRef}>
            {spanComponent && selectedItem ? (
              spanComponent(selectedItem[idKey])
            ) : (
              <span className="flex items-center">
                {prefixIcon && <span className="mr-2">{prefixIcon}</span>}
                <span className={`block truncate ${disabled ? 'text-accentText' : 'text-primaryText'} ${spanStyles}`}>
                  {selectedItem ? selectedItem[nameKey] : placeholder}
                </span>
              </span>
            )}
            {!hideDropDownIcon && (
              <span
                className={twMerge('pointer-events-none absolute inset-y-0 right-5 flex items-center pr-2', iconStyle)}>
                {trailingIcon ? (
                  trailingIcon
                ) : (
                  <DropDownChevronDownIcon className="h-[8px] w-[14px] text-gray-400" aria-hidden="true" />
                )}
              </span>
            )}
          </Listbox.Button>
          {renderOptions()}
        </div>
      </Listbox>
    </div>
  );
};

export default ListDropDown;
